import React, { useEffect, useRef } from 'react';
// @ts-ignore - internal lib entry, no type declarations.
import JitsiMeetJS from '../../../base/lib-jitsi-meet/_';
const JitsiTrackEvents = JitsiMeetJS.events.track;
/**
 * A theme-aware voice-waveform overlay for a video tile. Subscribes to the
 * Jitsi per-track audio level (0..1) — the same signal the audio indicator
 * uses, so no extra WebAudio graph — and paints one of three styles that pick
 * up the active theme's accent colors from CSS variables.
 *
 * Meant to be rendered as an absolutely-positioned child of a positioned tile
 * container (e.g. #largeVideoContainer or the local thumbnail).
 *
 * @param {IProps} props - Component props.
 * @returns {JSX.Element}
 */
export default function VoiceWaveOverlay({ audioTrack, role, variant }) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    // Live level (0..1) written by the track event, read by the rAF loop.
    const targetRef = useRef(0);
    useEffect(() => {
        targetRef.current = 0;
        const jitsiTrack = audioTrack?.jitsiTrack;
        const onLevel = (lvl) => {
            targetRef.current = lvl;
        };
        jitsiTrack?.on(JitsiTrackEvents.TRACK_AUDIO_LEVEL_CHANGED, onLevel);
        return () => {
            jitsiTrack?.off(JitsiTrackEvents.TRACK_AUDIO_LEVEL_CHANGED, onLevel);
        };
    }, [audioTrack]);
    useEffect(() => {
        const wrap = wrapRef.current;
        const canvas = canvasRef.current;
        if (!wrap || !canvas) {
            return undefined;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return undefined;
        }
        let w = 0, h = 0;
        let raf = 0;
        let level = 0;
        let t = 0;
        const phase = role === 'ai' ? 0.7 : 3.1;
        // Read theme accent colors (fall back to the DeepAI defaults).
        let accent = '#246FE5';
        let accent2 = '#5fe6ff';
        const readColors = () => {
            const cs = getComputedStyle(wrap);
            const a = cs.getPropertyValue('--accent').trim();
            const a2 = cs.getPropertyValue('--accent-2').trim();
            accent = a || '#246FE5';
            accent2 = a2 || '#5fe6ff';
        };
        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            const r = canvas.getBoundingClientRect();
            w = r.width;
            h = r.height;
            canvas.width = Math.max(1, Math.round(w * dpr));
            canvas.height = Math.max(1, Math.round(h * dpr));
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            readColors();
        };
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);
        resize();
        const hexA = (hex, alpha) => {
            let s = hex.replace('#', '');
            if (s.length === 3) {
                s = s.split('').map(c => c + c).join('');
            }
            const r = parseInt(s.slice(0, 2), 16);
            const g = parseInt(s.slice(2, 4), 16);
            const b = parseInt(s.slice(4, 6), 16);
            return `rgba(${r},${g},${b},${alpha})`;
        };
        const roundRect = (x, y, bw, bh, r) => {
            const rr = Math.min(r, bw / 2, bh / 2);
            ctx.beginPath();
            ctx.moveTo(x + rr, y);
            ctx.arcTo(x + bw, y, x + bw, y + bh, rr);
            ctx.arcTo(x + bw, y + bh, x, y + bh, rr);
            ctx.arcTo(x, y + bh, x, y, rr);
            ctx.arcTo(x, y, x + bw, y, rr);
            ctx.closePath();
        };
        const drawBars = () => {
            const n = role === 'ai' ? 48 : 22;
            const gap = 2;
            const bw = (w - (gap * (n - 1))) / n;
            const base = h - 8;
            const maxH = h * 0.32;
            const g = ctx.createLinearGradient(0, base - maxH, 0, base);
            g.addColorStop(0, accent2);
            g.addColorStop(1, accent);
            ctx.fillStyle = g;
            for (let i = 0; i < n; i++) {
                const k = (Math.sin((i * 0.5) + (t * 8) + phase) * 0.5) + 0.5;
                const bh = Math.max(2, level * maxH * (0.35 + (0.65 * k)));
                ctx.globalAlpha = 0.95;
                roundRect(i * (bw + gap), base - bh, bw, bh, bw / 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
        };
        // ── ai-cool visualizer: N overlapping center-weighted sine lines,
        //    additively blended for a glowing "AI voice" orb-strip.
        const N = role === 'ai' ? 40 : 20;
        const lines = [];
        for (let i = 0; i < N; i++) {
            lines.push({
                phase: i * 0.12,
                amp: 0.4 + (Math.random() * 0.6),
                freq: 1.4 + (Math.random() * 1.2),
                speed: 0.7 + (Math.random() * 0.9)
            });
        }
        const drawString = () => {
            const cx = w * 0.5;
            const centerY = h * 0.5;
            const spread = w * 0.24;
            const baseAmp = h * 0.22;
            const lvl = Math.min(1, 0.12 + level); // small idle + reactive
            const step = Math.max(4, Math.floor(w / 180));
            const grad = ctx.createLinearGradient(0, 0, w, 0);
            grad.addColorStop(0, hexA(accent, 0));
            grad.addColorStop(0.2, hexA(accent, 0.9));
            grad.addColorStop(0.5, hexA(accent2, 1));
            grad.addColorStop(0.8, hexA(accent, 0.9));
            grad.addColorStop(1, hexA(accent, 0));
            ctx.globalCompositeOperation = 'lighter';
            ctx.lineWidth = 1.4;
            ctx.shadowColor = hexA(accent, 0.9);
            ctx.shadowBlur = 12;
            ctx.strokeStyle = grad;
            for (let i = 0; i < N; i++) {
                const ln = lines[i];
                ctx.globalAlpha = 0.08 + ((i / N) * 0.14);
                ctx.beginPath();
                for (let x = 0; x <= w; x += step) {
                    const env = Math.exp(-Math.pow((x - cx) / spread, 2));
                    const y = centerY + (baseAmp * lvl * env * ln.amp
                        * Math.sin((x * ln.freq * 2 * Math.PI / w) + ln.phase + (t * ln.speed)));
                    x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
                }
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
            ctx.shadowBlur = 0;
            ctx.globalCompositeOperation = 'source-over';
        };
        const drawHalo = () => {
            const spread = (4 + (level * 30)).toFixed(1);
            const blur = (16 + (level * 44)).toFixed(1);
            const a = (0.22 + (level * 0.5)).toFixed(2);
            wrap.style.boxShadow
                = `0 0 ${blur}px ${spread}px ${hexA(accent, Number(a))}, `
                    + `inset 0 0 0 2px ${hexA(accent2, 0.55)}`;
        };
        const frame = () => {
            t += 0.016;
            level += (targetRef.current - level) * 0.2;
            if (variant === 'halo') {
                drawHalo();
            }
            else {
                ctx.clearRect(0, 0, w, h);
                if (variant === 'bars') {
                    drawBars();
                }
                else {
                    drawString();
                }
            }
            raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            wrap.style.boxShadow = '';
        };
    }, [variant, role]);
    return (React.createElement("div", { ref: wrapRef, style: {
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            // 映像より後ろ（DOM順で映像コンテナより前 + z:0）に置く。
            zIndex: 0
        } }, variant !== 'halo' && (React.createElement("canvas", { ref: canvasRef, style: { position: 'absolute', inset: 0, width: '100%', height: '100%' } }))));
}
